import { FC, useCallback, useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import TranslateIcon from '@mui/icons-material/Translate';
import useAuth from 'src/hooks/useAuth';
import useSettings from 'src/hooks/useSettings';
import useGoogleTranslate, { LANGUAGES } from 'src/hooks/useGoogleTranslate';
import { RootState } from 'src/redux/rootReducerType';
import {
  getUserDisplayName,
  getUserInitial,
} from 'src/components/NewRenterInterface/utils/NewRentersInterfaceUtils';
import {
  setElevatedAuth,
  selectElevatedAuthIsValid,
  selectHasPendingVerifications,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import elevatedAuthService from 'src/services/elevatedAuth.service';
import VerifyCodeModal from 'src/components/NewRenterInterface/AccountHub/VerifyCodeModal';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

const SettingsDrawer: FC<SettingsDrawerProps> = ({ open, onClose }) => {
  const { logout } = useAuth();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const { themeMode, toggleThemeMode } = useSettings();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLang, currentLanguageLabel, selectLanguage } =
    useGoogleTranslate();
  const isElevatedAuthValid = useSelector(selectElevatedAuthIsValid);
  const hasPendingVerifications = useSelector(selectHasPendingVerifications);
  const totpEnabled = useSelector(
    (state: RootState) =>
      state.newRenterInterfaceUser.accountHub.elevatedAuth.totp_enabled
  );
  const userEmail = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.email
  );
  const userEmailVerified = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.email_verified
  );
  const userPhoneVerified = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.phone_verified
  );
  const hasRealEmail = !!userEmail && !userEmail.endsWith('@prontohousing.io');
  const otpFirstChannel: 'email' | 'sms' = hasRealEmail ? 'email' : 'sms';
  // Only treat the user as having no verified contact when there are pending
  // verifications AND they have neither a verified real email nor a verified
  // phone — i.e. they truly cannot receive an OTP through any channel.
  const hasUsableContact =
    (hasRealEmail && userEmailVerified === true) || userPhoneVerified === true;
  const hasNoVerifiedContact = hasPendingVerifications && !hasUsableContact;

  // Controls whether the inline verify-it's-you modal is showing
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [showPendingVerifications, setShowPendingVerifications] =
    useState(false);

  const handleThemeChange = useCallback(
    (_: MouseEvent<HTMLElement>, newMode: string | null) => {
      if (newMode !== null && newMode !== themeMode) {
        toggleThemeMode();
      }
    },
    [themeMode, toggleThemeMode]
  );

  const handleLogout = useCallback(async () => {
    onClose();
    await logout();
  }, [logout, onClose]);

  const handleAccountHubClick = useCallback(async () => {
    // If already elevated, navigate immediately; otherwise show the verify modal.
    if (isElevatedAuthValid) {
      onClose();
      navigate(`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`);
      return;
    }
    if (hasNoVerifiedContact) {
      setShowPendingVerifications(true);
      return;
    }
    // Pre-fetch status so the modal has totp_enabled set in Redux before opening.
    try {
      const res = await elevatedAuthService.getStatus();
      dispatch(
        setElevatedAuth({
          verified: res.verified,
          expires_at: res.expires_at ?? undefined,
          totp_enabled: res.totp_enabled,
        })
      );
    } catch {
      // Non-fatal — modal will use whatever is in Redux already
    }
    setVerifyOpen(true);
  }, [dispatch, hasNoVerifiedContact, isElevatedAuthValid, navigate, onClose]);

  const handleVerified = useCallback(
    (expiresAt: string, method: 'otp' | 'totp'): void => {
      dispatch(
        setElevatedAuth({
          verified: true,
          expires_at: expiresAt,
          verified_at: new Date().toISOString(),
          method,
        })
      );
      setVerifyOpen(false);
      onClose();
      navigate(`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`);
    },
    [dispatch, navigate, onClose]
  );

  const handleVerifyCancel = useCallback(() => {
    setVerifyOpen(false);
  }, []);

  const userName = getUserDisplayName(user);
  const userInitial = getUserInitial(user);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: 300 } },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">Settings</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <List disablePadding>
          <ListItem sx={{ py: 2 }}>
            <ListItemText
              primary={
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <ListItemIcon sx={{ m: 0 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: 'primary.main',
                          userSelect: 'none',
                        }}
                      >
                        {userInitial}
                      </Typography>
                    </Box>
                  </ListItemIcon>
                  {userName}
                </Stack>
              }
              secondary={
                <ListItemButton
                  sx={{
                    pl: '40px',
                    ml: 1,
                    gap: 1,
                  }}
                  onClick={handleLogout}
                >
                  <ListItemIcon sx={{ m: 0 }}>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2">Logout</Typography>
                </ListItemButton>
              }
              slotProps={{
                primary: {
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  component: 'div',
                },
                secondary: { component: 'div' },
              }}
            />
          </ListItem>

          <Divider />

          <ListItemButton onClick={handleAccountHubClick}>
            <ListItemIcon>
              <ManageAccountsIcon />
            </ListItemIcon>
            <ListItemText primary="Account Hub" />
          </ListItemButton>

          <Divider />

          <ListItem>
            <ListItemIcon>
              <TranslateIcon />
            </ListItemIcon>
            <ListItemText
              primary="Language"
              secondary={currentLanguageLabel}
              slotProps={{ secondary: { variant: 'caption' } }}
            />
          </ListItem>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {LANGUAGES.map((lang) => (
                <ListItemButton
                  key={lang.code}
                  className="notranslate"
                  onClick={() => selectLanguage(lang.code)}
                  selected={currentLang === lang.code}
                  sx={{ py: 1 }}
                >
                  <ListItemText
                    primary={lang.label}
                    slotProps={{ primary: { variant: 'body2' } }}
                  />
                  {currentLang === lang.code && (
                    <CheckIcon fontSize="small" color="primary" />
                  )}
                </ListItemButton>
              ))}
            </Box>
          </Box>

          <Divider />

          <ListItem>
            <ListItemIcon>
              {themeMode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
            </ListItemIcon>
            <ListItemText primary="Theme" />
          </ListItem>
          <Box sx={{ px: 2, pb: 2 }}>
            <ToggleButtonGroup
              value={themeMode}
              exclusive
              onChange={handleThemeChange}
              fullWidth
              size="small"
            >
              <ToggleButton value="light">
                <LightModeIcon sx={{ mr: 1 }} fontSize="small" />
                Light
              </ToggleButton>
              <ToggleButton value="dark">
                <DarkModeIcon sx={{ mr: 1 }} fontSize="small" />
                Dark
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </List>
      </Drawer>

      {verifyOpen && !showPendingVerifications && (
        <VerifyCodeModal
          purpose="elevated_auth"
          totpEnabled={totpEnabled}
          preferredChannel={otpFirstChannel}
          dismissable
          onSuccess={handleVerified}
          onCancel={handleVerifyCancel}
          onUnverifiedContact={() => {
            setVerifyOpen(false);
            setShowPendingVerifications(true);
          }}
        />
      )}
      {showPendingVerifications && (
        <PendingVerificationsModal
          onDismiss={() => setShowPendingVerifications(false)}
        />
      )}
    </>
  );
};

export default SettingsDrawer;
